import type {
  ReportSupplementEntry,
  ReportSupplementGroupType,
  ReportSupplementSignOffType,
} from "../../types/reportTemplate";

export type { ReportSupplementGroupType } from "../../types/reportTemplate";

export interface ReportSupplementGroup {
  type: ReportSupplementGroupType;
  entries: ReportSupplementEntry[];
}

const GROUP_TYPE_ORDER: ReportSupplementGroupType[] = [
  "addendum",
  "amended",
  "corrected",
];

export function normalizeReportSupplementType(
  type: ReportSupplementSignOffType
): ReportSupplementGroupType {
  return type === "amendment" ? "amended" : type;
}

function supplementTimestamp(entry: ReportSupplementEntry): number {
  const timestamps = [entry.createdAt, entry.signedAt]
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value));
  return (
    timestamps.find((timestamp) => !Number.isNaN(timestamp)) ??
    Number.POSITIVE_INFINITY
  );
}

export function compareReportSupplementEntries(
  left: ReportSupplementEntry,
  right: ReportSupplementEntry
): number {
  const leftTimestamp = supplementTimestamp(left);
  const rightTimestamp = supplementTimestamp(right);
  if (leftTimestamp !== rightTimestamp) return leftTimestamp - rightTimestamp;
  const versionDifference = left.reportVersion - right.reportVersion;
  return versionDifference || left.id.localeCompare(right.id);
}

export function reportSupplementEntriesOfType(
  entries: ReportSupplementEntry[],
  type: ReportSupplementGroupType
): ReportSupplementEntry[] {
  return entries
    .filter(
      (entry) => normalizeReportSupplementType(entry.signOffType) === type
    )
    .sort(compareReportSupplementEntries);
}

export function groupReportSupplements(
  entries: ReportSupplementEntry[]
): ReportSupplementGroup[] {
  return GROUP_TYPE_ORDER.map((type) => ({
    type,
    entries: reportSupplementEntriesOfType(entries, type),
  }))
    .filter((group) => group.entries.length > 0)
    .sort((left, right) => {
      const leftTimestamp = supplementTimestamp(left.entries[0]);
      const rightTimestamp = supplementTimestamp(right.entries[0]);
      if (leftTimestamp !== rightTimestamp) {
        return leftTimestamp - rightTimestamp;
      }
      return (
        GROUP_TYPE_ORDER.indexOf(left.type) -
        GROUP_TYPE_ORDER.indexOf(right.type)
      );
    });
}
